import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';
import SearchBar from '~/shared/components/SearchBar';
import Loader from '~/shared/components/Loader';
import HeadlessTreeNode from '~/settings/components/ConfigurationsPage/ConfigurationTree/HeadlessTreeNode';
import { useConfigurationTreeInfinite } from '~/settings/components/ConfigurationsPage/ConfigurationTree/useConfigurationTreeInfinite';

import ConfigurationDetails from '../ConfigurationDetails/ConfigurationDetails';
import { useConfigurationActions } from '../hooks/useConfigurationActions';

import type { ItemInstance } from '@headless-tree/core';
import type {
  ConfigTreeNode,
  SelectedNodeData,
} from '~/settings/components/ConfigurationsPage/types';

interface VerticalLineConfig {
  top: number;
  height: number;
  left: number;
  key: string;
}

function calculateVerticalLines(items: ItemInstance<ConfigTreeNode>[]): VerticalLineConfig[] {
  const ITEM_HEIGHT = 64;
  const ITEM_GAP = 4;
  const ITEM_SPACING = ITEM_HEIGHT + ITEM_GAP;
  const CHEVRON_OFFSET = 32;
  const LEVEL_INDENT = 20;
  const BASE_LEFT_OFFSET = 22;

  const lines: VerticalLineConfig[] = [];

  for (let parentIndex = 0; parentIndex < items.length; parentIndex++) {
    const parentItem = items[parentIndex];
    const parentMeta = parentItem.getItemMeta();
    const parentLevel = parentMeta.level;
    const isExpanded = parentItem.isExpanded();
    const hasChildren = parentItem.isFolder();

    if (!isExpanded || !hasChildren) {
      continue;
    }

    let lastChildIndex = -1;
    for (let childIndex = parentIndex + 1; childIndex < items.length; childIndex++) {
      const childItem = items[childIndex];
      const childMeta = childItem.getItemMeta();
      const childLevel = childMeta.level;

      if (childLevel <= parentLevel) {
        break;
      }

      if (childLevel === parentLevel + 1) {
        lastChildIndex = childIndex;
      }
    }

    if (lastChildIndex === -1) {
      continue;
    }

    const top = parentIndex * ITEM_SPACING + CHEVRON_OFFSET;
    const bottom = lastChildIndex * ITEM_SPACING + ITEM_HEIGHT;
    const height = bottom - top;
    const left = parentLevel * LEVEL_INDENT + BASE_LEFT_OFFSET;

    lines.push({
      top,
      height,
      left,
      key: `line-${parentItem.getId()}`,
    });
  }

  return lines;
}

/** Row height (64) + gap (4) — must match list layout for scroll adjustment. */
const LIST_ITEM_SPACING = 64 + 4;

interface AddNewConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (selectedNodeId: string) => void;
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) {
    return;
  }
  if (typeof ref === 'function') {
    (ref as (value: T | null) => void)(value);
  } else {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

export default function AddNewConfigurationModal({
  isOpen,
  onClose,
  // onNext,
}: AddNewConfigurationModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { tree, rootId, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useConfigurationTreeInfinite({ instanceId: searchQuery.trim() || undefined });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const fetchNextPageRef = useRef({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    scrollLoadDisabled: false,
  });
  const prevItemsLengthRef = useRef(0);
  const scrollLoadDisabledRef = useRef(false);
  const hasUserScrolledRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const prevItemsLengthForScrollRef = useRef(0);
  const prevFetchingNextPageRef = useRef(false);
  const [scrollContainerReady, setScrollContainerReady] = useState(false);
  const [sentinelReady, setSentinelReady] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [selectedNodeData, setSelectedNodeData] = useState<SelectedNodeData | null>(null);

  const { handleSave, handleRevertField, handleRevertAll } = useConfigurationActions(
    selectedNodeData
      ? {
          id: selectedNodeData.id,
          name: selectedNodeData.name,
          level: selectedNodeData.level,
          clusterId: selectedNodeData.clusterId,
          instanceId: selectedNodeData.instanceId,
          accountId: selectedNodeData.accountId,
          region: selectedNodeData.region,
        }
      : { id: null, name: null },
    () => {}
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedNodeId(null);
      setStep('select');
      setSelectedNodeData(null);
      setScrollContainerReady(false);
      setSentinelReady(false);
      prevItemsLengthRef.current = 0;
      scrollLoadDisabledRef.current = false;
      hasUserScrolledRef.current = false;
      prevItemsLengthForScrollRef.current = 0;
      prevFetchingNextPageRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    hasUserScrolledRef.current = false;
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.trim()) {
      tree.setSearch(searchQuery);
    } else {
      tree.setSearch(null);
    }
  }, [searchQuery, tree]);

  const containerProps = tree.getContainerProps();
  const mergeContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollContainerRef.current = el;
      setRef(containerProps.ref as React.Ref<HTMLDivElement>, el);
      setScrollContainerReady(!!el);
    },
    [containerProps.ref]
  );
  const setSentinelRef = useCallback((el: HTMLDivElement | null) => {
    (loadMoreSentinelRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    setSentinelReady(!!el);
  }, []);

  const allItems = tree.getItems();

  // Manually filter items based on search query
  const items = useMemo(() => {
    if (!searchQuery.trim()) {
      return allItems;
    }

    const searchLower = searchQuery.toLowerCase();
    const matchingItemIds = new Set<string>();
    const itemsToInclude = new Set<string>();

    allItems.forEach((item) => {
      const itemName = item.getItemName().toLowerCase();
      if (itemName.includes(searchLower)) {
        matchingItemIds.add(item.getId());
        itemsToInclude.add(item.getId());
      }
    });

    // Include parent items of matching items to preserve tree structure
    allItems.forEach((item, index) => {
      if (matchingItemIds.has(item.getId())) {
        const itemMeta = item.getItemMeta();
        let currentLevel = itemMeta.level;

        for (let i = index - 1; i >= 0; i--) {
          const potentialParent = allItems[i];
          const parentMeta = potentialParent.getItemMeta();
          const parentLevel = parentMeta.level;

          if (parentLevel === currentLevel - 1) {
            itemsToInclude.add(potentialParent.getId());
            currentLevel = parentLevel;
            if (parentLevel === 0) {
              break;
            }
          } else if (parentLevel >= currentLevel) {
            break;
          }
        }
      }
    });

    return allItems.filter((item) => itemsToInclude.has(item.getId()));
  }, [allItems, searchQuery]);

  if (items.length < prevItemsLengthRef.current) {
    scrollLoadDisabledRef.current = true;
  } else if (items.length > prevItemsLengthRef.current) {
    scrollLoadDisabledRef.current = false;
  }
  prevItemsLengthRef.current = items.length;
  fetchNextPageRef.current = {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    scrollLoadDisabled: scrollLoadDisabledRef.current,
  };

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const wasFetchingNextPage = prevFetchingNextPageRef.current;
    prevFetchingNextPageRef.current = isFetchingNextPage;

    const prevLen = prevItemsLengthForScrollRef.current;
    const justFinishedFetch =
      wasFetchingNextPage && !isFetchingNextPage && items.length > prevLen && prevLen > 0;
    if (justFinishedFetch) {
      el.scrollTop = lastScrollTopRef.current + (items.length - prevLen) * LIST_ITEM_SPACING;
    }

    lastScrollTopRef.current = el.scrollTop;
    prevItemsLengthForScrollRef.current = items.length;
  }, [items.length, isFetchingNextPage]);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const sentinelEl = loadMoreSentinelRef.current;
    if (!scrollEl || !sentinelEl || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const r = fetchNextPageRef.current;
        if (r.scrollLoadDisabled) {
          return;
        }
        if (entry?.isIntersecting) {
          if (!hasUserScrolledRef.current) {
            return;
          }
          r.fetchNextPage();
        }
      },
      { root: scrollEl, rootMargin: '200px', threshold: 0 }
    );
    const rafId = requestAnimationFrame(() => {
      observer.observe(sentinelEl);
    });
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    items.length,
    scrollContainerReady,
    sentinelReady,
  ]);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) {
      return;
    }

    const handleScroll = () => {
      hasUserScrolledRef.current = true;
      const r = fetchNextPageRef.current;
      if (r.scrollLoadDisabled) {
        return;
      }
      const el = scrollContainerRef.current;
      if (!el) {
        return;
      }
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 200;
      if (nearBottom && r.hasNextPage && !r.isFetchingNextPage) {
        r.fetchNextPage();
      }
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [items.length, scrollContainerReady]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    setStep('select');
  };

  const handleSelectNode = (nodeId: string) => {
    setSelectedNodeId(nodeId);

    const item = allItems.find((i) => i.getId() === nodeId);
    if (item) {
      const itemData = item.getItemData();
      setSelectedNodeData({
        id: itemData.id,
        name: itemData.name,
        type: itemData.type,
        level: itemData.level,
        clusterId: itemData.clusterId,
        instanceId: itemData.instanceId,
        accountId: itemData.accountId,
        region: 'us-east-1',
      });
      setStep('details');
    }
  };

  return (
    <Dialog
      open={isOpen}
      as="div"
      className="relative z-[99] focus:outline-none "
      onClose={handleClose}
    >
      <div className="fixed inset-0 p-4 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px]">
        <div className="flex items-center justify-center p-8  h-full">
          <DialogPanel
            transition
            className={`flex my-auto max-h-full h-full w-full max-w-5xl flex-col rounded-xl bg-white backdrop-blur-2xl duration-300 overflow-hidden data-closed:transform-[scale(95%)] data-closed:opacity-0`}
          >
            <div className={`flex flex-col gap-4  min-h-full  ${step === 'select' ? 'p-6' : ''} `}>
              <div className={`${step === 'details' ? 'px-6 pt-6' : ''} py2.5`}>
                <DialogHeader title="Add new configuration" />
              </div>
              {step === 'select' ? (
                <div className="flex flex-col flex-1 min-h-0  ">
                  <div className="w-full mb-2">
                    <SearchBar
                      size="md"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full"
                    />
                  </div>

                  <div
                    ref={mergeContainerRef}
                    className="flex-1 overflow-y-auto side-panel-scrollbar"
                    style={{ direction: 'rtl', overflowAnchor: 'auto' }}
                    {...Object.fromEntries(
                      Object.entries(containerProps).filter(([k]) => k !== 'ref')
                    )}
                  >
                    <div style={{ direction: 'ltr' }}>
                      {items.length === 0 ? (
                        isLoading ? (
                          <div className="flex flex-1 justify-center items-center h-full">
                            <Loader variant="xl" />
                          </div>
                        ) : (
                          <div className="p-4 text-center text-primary-900">
                            <Typography variant="p2">No items found</Typography>
                          </div>
                        )
                      ) : (
                        <div className="flex flex-col gap-1 relative">
                          {calculateVerticalLines(items).map(({ top, height, left, key }) => (
                            <div
                              key={key}
                              className="absolute w-[1px] bg-primary-100 z-20"
                              style={{
                                left: `${left}px`,
                                top: `${top}px`,
                                height: `${height}px`,
                              }}
                            />
                          ))}
                          {items.map((item) => (
                            <HeadlessTreeNode
                              key={item.getId()}
                              item={item}
                              selectedNodeId={selectedNodeId}
                              onSelect={handleSelectNode}
                              hasChevron={item.isFolder()}
                              showSelectButton={true}
                              count={
                                item.isFolder() ? item.getItemData().children?.length : undefined
                              }
                              showPlus={
                                item.isFolder() &&
                                rootId !== null &&
                                item.getId() === rootId &&
                                hasNextPage
                              }
                              hideHoverEffect={false}
                            />
                          ))}
                          <div ref={setSentinelRef} className="min-h-[10px] w-full" aria-hidden />
                          {isFetchingNextPage && (
                            <div className="flex justify-center py-4">
                              <Loader variant="sm" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter onCancel={handleClose} />
                </div>
              ) : (
                <div className="h-full  flex flex-col overflow-y-auto  side-panel-scrollbar">
                  {selectedNodeData && (
                    <ConfigurationDetails
                      nodeData={selectedNodeData}
                      selectNode={() => {}}
                      onBack={handleBack}
                      onCancel={handleClose}
                      onSave={handleSave}
                      onRevertField={handleRevertField}
                      onRevertAll={handleRevertAll}
                      isAddNewConfigurationModal={true}
                    />
                  )}
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

function DialogHeader({ title }: { title: string }) {
  return (
    <div className="flex flex-col font-family-outfit">
      <DialogTitle as="h3" className="text-2xl 2xl:text-[28px] font-[500]">
        {title}
      </DialogTitle>
    </div>
  );
}

function DialogFooter({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="mt-auto flex gap-2 px-6 py-4 -ml-6 -mr-6 -mb-6 border-t border-primary-70">
      <div className="flex gap-2">
        <Button
          onClick={onCancel}
          variant="third"
          className="px-5"
          leftIcon={<XMarkIcon className="w-4 h-4" />}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
